import React, { useEffect, useRef, useState } from 'react';
import { getTheme, nextTheme, setTheme } from '../lib/theme';
import Icon from './Icon';
import Avatar from './Avatar';
import RequestDialog from './RequestDialog';
import BNMark from './BNMark';
import { api } from '../lib/api';

const ICON_FOR = { home: 'home', news: 'news', people: 'people', groups: 'groups', copilot: 'copilot', admin: 'summary', promote: 'megaphone', network: 'intro' };
const THEME_LABELS = { system: 'Theme: match device', light: 'Theme: light', dark: 'Theme: dark' };

/* Icon and word on every item: testers couldn't tell what several icons meant,
   and on a desktop there's room to say. (Narrow windows fall back to icons with
   a hover label.) Your own things — profile, tour, theme, sign out — live under
   your avatar, so the menu is only places to go. */
const ITEMS = [
  ['home', 'Home'],
  // Introductions and the conversations they become are one place.
  ['people', 'People'],
  ['groups', 'Groups'],
  // Beta: the map of who's connected to whom.
  ['network', 'Network'],
  ['promote', 'Promote'],
  // Opens a panel, not a page.
  ['copilot', 'Copilot'],
];

function AccountMenu({ me, onProfile, onTour, onSignOut }) {
  const [open, setOpen] = useState(false);
  const [theme, setThemeState] = useState(getTheme);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const pick = (fn) => () => { setOpen(false); fn(); };
  return (
    <div className="acct" ref={ref}>
      {open && (
        <div className="acct-menu" role="menu">
          <button role="menuitem" onClick={pick(onProfile)}><Icon name="profile" size={18} /> My profile</button>
          <button role="menuitem" onClick={pick(onTour)}><span className="acct-q" aria-hidden="true">?</span> Take the tour</button>
          <button role="menuitem" onClick={() => { const n = nextTheme(theme); setTheme(n); setThemeState(n); }}>
            <Icon name={theme === 'dark' ? 'moon' : theme === 'light' ? 'sun' : 'device'} size={18} /> {THEME_LABELS[theme]}
          </button>
          <button role="menuitem" className="acct-out" onClick={pick(onSignOut)}><Icon name="signout" size={18} /> Sign out</button>
        </div>
      )}
      <button className="acct-btn" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}
        aria-label="Your account: profile, tour, theme, sign out">
        <Avatar person={me} className="sidenav-av" />
        <span className="sidenav-label acct-name">{me?.name?.split(' ')[0] || 'You'}</span>
      </button>
    </div>
  );
}

/* The logo carries the community's pulse: everyone on B/N, and how many are
   here right now. Refreshed every minute. */
function BrandPulse() {
  const [p, setP] = useState(null);
  useEffect(() => {
    let live = true;
    const load = () => api.pulse().then((x) => live && setP(x)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { live = false; clearInterval(t); };
  }, []);
  return (
    <div className="sidenav-brand bn-brand" title={p ? `${p.members} builders on B/N · ${p.online} online now` : "Builders' Network"}>
      <span className="bn-logo" aria-hidden="true">
        <BNMark size={52} />
        {p && <b className={`bn-count${p.members >= 1000 ? ' long' : ''}`}>{p.members >= 1000 ? `${(p.members / 1000).toFixed(1)}k` : p.members}</b>}
        {p && p.online > 0 && <em className="bn-online">● {p.online}</em>}
      </span>
      <span className="bn-name sidenav-label">Builders' Network</span>
      <span className="sr-only">{p ? `${p.members} builders, ${p.online} online now` : ''}</span>
    </div>
  );
}

export default function SideNav({ view, setView, counts, me, onSignOut, copilotOpen, onCopilot, isAdmin, onTour, showNews, notify }) {
  const [requesting, setRequesting] = useState(false);
  const items = [...ITEMS];
  if (showNews) items.push(['news', 'News']);
  if (isAdmin) items.push(['admin', 'Admin']);
  return (
    <nav className="sidenav" aria-label="Main">
      <BrandPulse />

      <div className="sidenav-items">
        {items.map(([key, label]) => {
          const on = key === 'copilot' ? copilotOpen : view === key || (key === 'admin' && view === 'matching');
          return (
            <button
              key={key}
              data-tour={`nav-${key}`}
              className={`sidenav-item${on ? ' on' : ''}`}
              onClick={() => (key === 'copilot' ? onCopilot() : setView(key))}
              aria-current={key !== 'copilot' && on ? 'page' : undefined}
              aria-expanded={key === 'copilot' ? Boolean(copilotOpen) : undefined}
              aria-label={label}
            >
              <Icon name={ICON_FOR[key]} size={22} />
              <span className="sidenav-label">{label}{key === 'network' && <em className="beta-tag">Beta</em>}</span>
              {counts?.[key] > 0 && <em className="sidenav-count">{counts[key]}</em>}
              <span className="sidenav-tip" aria-hidden="true">{label}</span>
            </button>
          );
        })}
      </div>

      {/* Writing a post happens in the box at the top of Home. This one is for
          telling us what B/N should do next. */}
      <button className="btn apply sidenav-post sidenav-request" onClick={() => setRequesting(true)}
        aria-label="Request a feature or report a bug">
        <Icon name="bulb" size={20} className="sidenav-postic" />
        <span className="sidenav-label">Request</span>
        <span className="sidenav-tip" aria-hidden="true">Request a feature or report a bug</span>
      </button>
      <p className="sidenav-hint sidenav-label">Ask for a feature or report a bug.</p>

      <svg className="sidenav-art" viewBox="0 0 340 300" aria-hidden="true">
        <g className="art-lines">
          <line x1="20" y1="250" x2="120" y2="180" /><line x1="120" y1="180" x2="240" y2="210" />
          <line x1="120" y1="180" x2="160" y2="70" /><line x1="160" y1="70" x2="280" y2="100" />
          <line x1="240" y1="210" x2="280" y2="100" /><line x1="40" y1="120" x2="120" y2="180" />
          <line x1="40" y1="120" x2="160" y2="70" /><line x1="240" y1="210" x2="200" y2="290" />
        </g>
        <g className="art-dots">
          <circle cx="20" cy="250" r="4" /><circle cx="120" cy="180" r="6" /><circle cx="240" cy="210" r="4" />
          <circle cx="160" cy="70" r="5" /><circle cx="280" cy="100" r="3" /><circle cx="40" cy="120" r="3" /><circle cx="200" cy="290" r="4" />
        </g>
      </svg>

      <div className="sidenav-me" data-tour="nav-profile">
        <AccountMenu me={me} onProfile={() => setView('profile')} onTour={onTour} onSignOut={onSignOut} />
      </div>
      {requesting && <RequestDialog notify={notify} onClose={() => setRequesting(false)} />}
    </nav>
  );
}
